import { ChangeEvent, useRef, useState } from "react";
import { uploadVanLoad } from "../../services";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { formatDateTimeBkk } from "../../utils/date";
import type {
  VanLoadSkippedVan,
  VanLoadUploadData,
  VanLoadVanResult,
} from "../../types/van-load.type";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// รองรับ Excel (.xlsx/.xlsm) และ CSV ตาม API van-load/upload
const ACCEPTED_TYPES =
  ".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,text/csv";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB ตามที่ API กำหนด

// หนึ่งแถวในตารางผล — รวมรถที่เขียนกับรถที่ถูกข้ามไว้ที่เดียว เพราะ ผจก. อ่านผล
// ของ "ไฟล์ที่เพิ่งอัปโหลด" ไม่ใช่ผลของสองลิสต์ที่แยกกัน
type ResultRow = {
  key: string;
  routeLabel: string;
  status: string;
  severity: "written" | "unchanged" | "skipped";
  note: string;
};

const skippedNote = (van: VanLoadSkippedVan): string => {
  if (van.reason_code !== "ALREADY_SOLD") return van.reason;
  // เวลาบิลจาก API เป็น ISO (UTC) — ผจก. ต้องอ่านเป็นเวลาไทยเสมอ
  const billedAt = formatDateTimeBkk(van.first_bill_at, "HH:mm");
  const parts = [billedAt ? "ขายร้านแรกแล้วเมื่อ " + billedAt + " น." : "ขายร้านแรกไปแล้ว"];
  if (van.first_bill_no) parts.push("บิลแรก " + van.first_bill_no);
  return parts.join(" · ");
};

const writtenRow = (van: VanLoadVanResult): ResultRow => {
  if (van.skipped_identical) {
    return {
      key: van.pathway_uuid,
      routeLabel: van.route_code,
      status: "ไม่เปลี่ยนแปลง",
      severity: "unchanged",
      note: "ข้อมูลเหมือนไฟล์เดิม ไม่ได้เขียนทับ",
    };
  }

  const zeroedNote = van.items_zeroed ? " · ตัดออก " + van.items_zeroed + " รายการ" : "";
  return {
    key: van.pathway_uuid,
    routeLabel: van.route_code,
    status: van.deferred ? "บันทึกแล้ว (รอเช็คอิน)" : "อัปเดตแล้ว",
    severity: "written",
    note: van.deferred
      ? van.items_set + " รายการ — จะมีผลกับสต๊อกตอนเช็คอิน"
      : van.items_set + " รายการ" + zeroedNote,
  };
};

const skippedRow = (van: VanLoadSkippedVan, index: number): ResultRow => ({
  key: "skipped-" + van.warehouse_code + "-" + index,
  routeLabel: van.route_code ?? van.warehouse_code,
  status: "ข้าม",
  severity: "skipped",
  note: skippedNote(van),
});

// รถที่ถูกข้ามขึ้นก่อนเสมอ — เป็นแถวเดียวที่ ผจก. ต้องลงมือทำอะไรต่อ
const buildRows = (data: VanLoadUploadData): ResultRow[] => [
  ...(data.skipped_vans ?? []).map(skippedRow),
  ...(data.vans ?? []).map(writtenRow),
];

const STATUS_COLOR: Record<ResultRow["severity"], "warning" | "success" | "default"> = {
  skipped: "warning",
  written: "success",
  unchanged: "default",
};

function ResultTable({ rows }: { rows: ResultRow[] }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>สาย</TableCell>
            <TableCell>สถานะ</TableCell>
            <TableCell>รายละเอียด</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{row.routeLabel}</TableCell>
              <TableCell>
                <Chip size="small" label={row.status} color={STATUS_COLOR[row.severity]} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {row.note}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function UploadStock() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<VanLoadUploadData | null>(null);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);

  const handleSelectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    // อนุญาตให้เลือกไฟล์เดิมซ้ำได้ (onChange ไม่ fire ถ้าค่าเดิม)
    event.target.value = "";

    if (selected && selected.size > MAX_FILE_SIZE) {
      setFile(null);
      setError("ไฟล์มีขนาดเกิน 10MB");
      return;
    }

    setError(null);
    setFile(selected);
  };

  const handleConfirm = async () => {
    if (!file) return;

    setUploading(true);
    try {
      // ความล้มเหลวของ axios จะถูกจัดการโดย response interceptor (global error)
      const { data } = await uploadVanLoad(file);
      const uploaded = data.data;
      const skipped = uploaded.skipped_vans ?? [];
      const vans = uploaded.vans ?? [];
      const written = vans.filter((van) => !van.skipped_identical);

      setResult(uploaded);

      if (skipped.length > 0) {
        // สายที่ถูกข้ามจะวิ่งทั้งวันด้วยสต๊อกชุดเดิม ถ้าแจ้งด้วย toast ที่หายไปเอง
        // ผจก. จะไม่รู้ตัวจนจบวัน — จึงบังคับให้กดรับทราบ
        setAcknowledgeOpen(true);
      } else if (written.length === 0) {
        pushGlobalSuccess("ไฟล์เหมือนเวอร์ชันปัจจุบัน ไม่มีการเปลี่ยนแปลง");
      } else {
        const totalRows = vans.reduce((sum, van) => sum + van.row_count, 0);
        pushGlobalSuccess(
          "อัปโหลดสำเร็จ: " + written.length + " คัน, รวม " + totalRows + " รายการ",
        );
      }
      setFile(null);
    } catch {
      // response interceptor แสดง global error (รวม 415/422 ฯลฯ) ให้แล้ว
      // กลืน rejection ตรงนี้เพื่อไม่ให้กลายเป็น unhandled error (CRA overlay)
    } finally {
      setUploading(false);
    }
  };

  const skippedCount = result?.skipped_vans?.length ?? 0;
  const rows = result ? buildRows(result) : [];

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Typography variant="h6" mb={2}>
        อัปโหลดสต๊อก (Vanload)
      </Typography>

      <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              เลือกไฟล์ Excel (.xlsx/.xlsm) หรือ CSV ขนาดไม่เกิน 10MB —
              ระบบจะอ่านวันที่และสาขาจากในไฟล์
            </Typography>

            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleSelectFile}
              style={{ display: "none" }}
            />

            <Box>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                sx={{ whiteSpace: "nowrap" }}
              >
                เลือกไฟล์
              </Button>
            </Box>

            {file && (
              <Typography variant="body2" color="text.secondary">
                ไฟล์ที่เลือก: {file.name}
              </Typography>
            )}

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}

            <Box>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleConfirm}
                disabled={!file || uploading}
                sx={{ whiteSpace: "nowrap" }}
              >
                ยืนยันการอัปโหลด
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {result && (
        <Card sx={{ boxShadow: "none", borderRadius: "12px", mt: 2 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="subtitle1" mb={1}>
              ผลการอัปโหลด — วันที่ {result.stock_date}
            </Typography>
            {skippedCount > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                มี {skippedCount} สายที่ไม่ได้อัปเดต — สต๊อกของสายนั้นยังเป็นค่าเดิม
              </Alert>
            )}
            <ResultTable rows={rows} />
          </CardContent>
        </Card>
      )}

      {/* ตารางผลค้างอยู่ในหน้าหลังปิด dialog ให้ย้อนดูหรือแคปหน้าจอส่งต่อได้ */}
      <Dialog
        open={acknowledgeOpen}
        onClose={() => setAcknowledgeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>มีสายที่ไม่ได้อัปเดต</DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            สต๊อกของสายด้านล่างยังเป็นค่าเดิม ถ้าต้องแก้ ต้องไปแก้ที่ใบตรวจนับ/บัญชี
          </Alert>
          <ResultTable rows={rows} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAcknowledgeOpen(false)}>
            รับทราบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
